import { codeableConcept } from '@seen/core/codeableConcept'
import { featureFlags } from '@seen/core/featureFlags'
import { ExtendedObservation } from '@seen/core/models/observation'
import { cn } from '@seen/ui'
import type { Editor } from '@tiptap/react'
import { type JSX, useEffect, useRef, useState } from 'react'
import { ObservationLastChip } from '#src/components/rich-text-editor/ObservationNode/ObservationHistory.tsx'
import {
  firstLineOf,
  textContent,
} from '#src/components/rich-text-editor/ObservationNode/observationLastValue.ts'
import { ObservationNodeFrame } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFrame.tsx'
import { ObservationRichTextEditor } from '#src/components/rich-text-editor/ObservationNode/ObservationRichTextEditor.tsx'
import { CHOOSE_PARTICIPANT } from '#src/components/rich-text-editor/ObservationNode/observationSubject.ts'
import { ObservationNoValue } from '#src/components/rich-text-editor/ObservationNode/ObservationWrapper.tsx'
import type {
  ObservationCaptureViewProps,
  ObservationNodeAttributes,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useNodeRecord } from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useObservationHistory } from '#src/hooks/useObservationHistory.ts'
import { extractTextFromHTML } from '#src/utils/html.ts'

export function ObservationNodeTextInput({
  capture,
  ...props
}: ObservationCaptureViewProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const attributes = props.node.attrs as ObservationNodeAttributes

  const { participant, noParticipant } = capture
  // Answered only where the note can be changed: not in a signed or
  // posted note, nor for a read-only role.
  const locked = isReadOnly || noParticipant || !capture.editable

  const practitioner = useCurrentPractitioner()
  const { create } = useNodeRecord()

  const [loading, setLoading] = useState(false)

  const [value, setValue] = useState<string | null>(null)
  const editorRef = useRef<Editor | null>(null)

  // Narratives are often carried forward and edited: the last one is offered
  // to pull in, unrecorded until the field is left, as typing is.
  const inlineCapture = useFeatureFlag(featureFlags.structuredCapture)
  const [lastObservation] = useObservationHistory({
    participant: inlineCapture ? participant : undefined,
    coding: { system: attributes.system, code: attributes.code },
    limit: 1,
  })
  const lastText = lastObservation?.valueString
  const isEmpty = !value || !extractTextFromHTML(value).trim()

  useEffect(() => {
    if (attributes.autofocus) {
      props.updateAttributes({ autofocus: false })
    }
  }, [attributes.autofocus, props])

  if (!props.editor.isEditable) {
    return <ObservationNoValue label={attributes.codingDisplay} />
  }

  return (
    <ObservationNodeFrame
      attributes={attributes}
      updateAttributes={props.updateAttributes}
      blockedReason={noParticipant ? CHOOSE_PARTICIPANT : undefined}
      layout="block"
      participant={participant}
    >
      <div
        className="mr-0.75" // Accounts for the shift below.
      />
      <ObservationRichTextEditor
        placeholder="Add text…"
        editorRef={editorRef}
        disabled={loading || locked}
        onChange={setValue}
        autofocus={attributes.autofocus ? 'end' : false}
        onBlur={async () => {
          if (!value?.trim()) {
            return
          }
          setLoading(true)
          await create(
            ExtendedObservation.fromValueString({
              value,
              code: codeableConcept({
                code: attributes.code,
                system: attributes.system,
                display: attributes.codingDisplay,
              }),
              participant,
              practitioner: practitioner.reference,
              status: capture.recordStatus,
            })
          )
          setLoading(false)
        }}
        className={cn(
          'min-w-[8rem] max-w-full', // Sized to its text, wrapping only once the text needs the line's width.
          '-ml-[0.1875rem]' // For visual alignment when the content goes onto multiple lines.
        )}
      />
      {isEmpty && lastText && (
        <ObservationLastChip
          observation={lastObservation}
          shown={firstLineOf(lastText)}
          disabled={loading || locked}
          onRepeat={
            locked
              ? undefined
              : () => {
                  const editor = editorRef.current
                  if (!editor || editor.isDestroyed) {
                    return
                  }
                  // Filled as if typed; recorded when the field is left.
                  editor.commands.setContent(textContent(lastText), {
                    emitUpdate: true,
                  })
                  editor.commands.focus('end')
                }
          }
        />
      )}
    </ObservationNodeFrame>
  )
}
